/**
 * Фильтры списка заказов, которые живут в URL.
 *
 * Источник истины — query-строка, а не useState: иначе F5 возвращал
 * страницу к значениям по умолчанию, а ссылкой на выборку нельзя
 * было поделиться. Умолчания вкладки и страницы в URL не пишем, чтобы он
 * оставался коротким: `/orders`, а не `/orders?tab=active&page=1`.
 * Исключение — даты: если в URL нет ни одной, страница подставляет сегодняшний
 * день и дописывает его в URL, иначе ссылка и F5 означали бы «заказы за всё
 * время». Одна дата без второй — законный открытый диапазон.
 */

export type OrdersTab = 'active' | 'closed'
export type OrdersDateType = 'delivery' | 'creation'

export interface OrdersSearch {
  tab?: OrdersTab | undefined
  search?: string | undefined
  status?: number | undefined
  deliveryType?: string | undefined
  courierId?: number | undefined
  dateType?: OrdersDateType | undefined
  /** Дата «с» в формате YYYY-MM-DD (календарный день) */
  dateFrom?: string | undefined
  /** Дата «по» в формате YYYY-MM-DD (календарный день) */
  dateTo?: string | undefined
  page?: number | undefined
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const readText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

/**
 * Числа в query приходят строками, но при навигации внутри приложения
 * роутер может передать и number — поддерживаем оба варианта.
 */
const readPositiveInt = (value: unknown): number | undefined => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value)
        : Number.NaN

  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

const readIsoDate = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !ISO_DATE_PATTERN.test(value)) return undefined

  const [yearPart, monthPart, dayPart] = value.split('-')
  const year = Number(yearPart)
  const month = Number(monthPart)
  const day = Number(dayPart)
  const date = new Date(year, month - 1, day)
  const isValid =
    date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day

  return isValid ? value : undefined
}

/**
 * Приводит query-строку к известному набору фильтров.
 * Мусор в URL отбрасываем, а не роняем страницу.
 */
export const validateOrdersSearch = (
  search: Record<string, unknown>
): OrdersSearch => {
  const page = readPositiveInt(search.page)

  return {
    tab: search.tab === 'closed' ? 'closed' : undefined,
    search: readText(search.search),
    status: readPositiveInt(search.status),
    deliveryType: readText(search.deliveryType),
    courierId: readPositiveInt(search.courierId),
    dateType: search.dateType === 'creation' ? 'creation' : undefined,
    dateFrom: readIsoDate(search.dateFrom),
    dateTo: readIsoDate(search.dateTo),
    // Первая страница — значение по умолчанию, в URL её не дублируем.
    page: page && page > 1 ? page : undefined,
  }
}

/** YYYY-MM-DD из URL → дата в локальном календаре (как её отдаёт DatePicker). */
export const isoDateToDate = (value?: string): Date | null => {
  if (!value) return null

  const [yearPart, monthPart, dayPart] = value.split('-')
  const year = Number(yearPart)
  const month = Number(monthPart)
  const day = Number(dayPart)
  if (!year || !month || !day) return null

  return new Date(year, month - 1, day)
}

/** Дата из DatePicker → YYYY-MM-DD для URL. */
export const dateToIsoDate = (date: Date | null): string | undefined => {
  if (!date) return undefined

  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Сегодняшний день по МСК в формате YYYY-MM-DD — дефолт фильтра дат.
 * Считаем от UTC+3, а не от таймзоны браузера: сутки доставки московские,
 * иначе логист из другого пояса видел бы «сегодня» не тот день.
 * Перехода на летнее время в МСК нет, поэтому сдвиг константный.
 */
export const todayIsoDateMsk = (): string =>
  new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10)
